<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { detectDeviceType } from '@/lib/utils'
import type { AnalysisModeEvents } from '@/types'
import { useAssessmentPopupStore } from '@/stores/assessmentPopupState'
import ExitAssessment from '@/components/modals/Login/ExitAssessment.vue'
import InitialAssessment from '@/components/modals/Login/InitialAssessment.vue'
import type { Player } from '@acepoker/contract'
import { useRouter } from 'vue-router'
import { getAnalysisModeUrlUtil } from './utils'

const store = useAssessmentPopupStore()
const router = useRouter()

const isMobile = ref(detectDeviceType() === 'mobile')
const popupRef = ref<HTMLDivElement | null>(null)

const navigateToHome = () => {
  router.push({ name: 'home' })
}

const iframeUrl = computed(() => {
  const { playerId, playerEmail, url, slide } = store.popupProps

  return getAnalysisModeUrlUtil('popup', playerId, 'mobile', playerEmail, url, slide)
})

const handleResize = () => {
  isMobile.value = detectDeviceType() === 'mobile'
}

const handleMessage = (event: MessageEvent<AnalysisModeEvents>) => {
  if (event.data?.type !== 'closePopup') return
  store.close()
  if (event.data?.payload?.openEmailSavePopup) {
    store.showCompletionAssessment()
  } else {
    navigateToHome()
  }
}

const onAssessmentSuccess = (player: Player) => {
  store.close()
  navigateToHome()
}

const continueWithoutSavingHandler = () => {
  store.close()
  navigateToHome()
}

const onExitAssessmentClose = () => {
  store.showExitAssessment = false
  // keep playing:
  if (!router.currentRoute.value.path.startsWith('/room/')) {
    router.push({ name: 'AssessmentModeJoin' })
  }
}

const onInitialAssessmentClose = () => {
  store.showInitialAssessment = false
  navigateToHome()
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('message', handleMessage)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('message', handleMessage)
})
</script>

<template>
  <teleport to="body">
    <!-- Backdrop with blur effect -->
    <div v-if="store.isVisible" class="fixed inset-0 z-90 bg-black/30 backdrop-blur-sm"></div>

    <!-- Popup content -->
    <div v-if="store.isVisible" class="fixed inset-0 z-100 flex items-center justify-center">
      <div
        v-if="!isMobile"
        ref="popupRef"
        class="relative mx-4 w-full max-w-[560px] overflow-hidden rounded-lg bg-[#0D1526] shadow-xl"
      >
        <iframe
          :src="iframeUrl"
          class="h-[730px] w-full rounded-lg border-none"
          allowfullscreen
          title="Analysis Mode"
        />
      </div>
      <div v-else ref="popupRef" class="relative h-dvh w-screen">
        <iframe
          :src="iframeUrl"
          class="h-full w-full border-none"
          allowfullscreen
          title="Analysis Mode"
        />
      </div>
    </div>
  </teleport>

  <exit-assessment
    v-if="store.showExitAssessment"
    :isOpen="store.showExitAssessment"
    :onSuccess="onAssessmentSuccess"
    :onContinueWithoutSaving="continueWithoutSavingHandler"
    :onClose="onExitAssessmentClose"
  />

  <initial-assessment
    v-if="store.showInitialAssessment"
    :isOpen="store.showInitialAssessment"
    :onSuccess="onAssessmentSuccess"
    :onContinueWithoutSaving="continueWithoutSavingHandler"
    :onClose="onInitialAssessmentClose"
  />
</template>
